import { useEffect, useRef, useCallback } from 'react';
// import { trackEngagement, trackEvent, getCurrentPage } from '../services/analytics';

// Temporary stub functions to prevent analytics errors
const trackEngagement = () => {};
const trackEvent = () => {};
const getCurrentPage = () => 'unknown';

export const useMicroInteractionTracking = (componentName, options = {}) => {
  const {
    trackClicks = true,
    trackHovers = true,
    trackFocus = true,
    trackScrolling = false,
    trackKeystrokes = false,
    trackMouseMovement = false,
    throttleMs = 1000
  } = options;

  const interactionCount = useRef(0);
  const lastInteractionTime = useRef(Date.now());
  const throttleTimers = useRef({});
  const elementRef = useRef(null);

  // Throttle function to limit event frequency
  const throttle = useCallback((key, callback, delay = throttleMs) => {
    if (throttleTimers.current[key]) {
      clearTimeout(throttleTimers.current[key]);
    }
    
    throttleTimers.current[key] = setTimeout(() => {
      callback();
      delete throttleTimers.current[key];
    }, delay);
  }, [throttleMs]);

  // Track click interactions
  const trackClick = useCallback((event) => {
    if (!trackClicks) return;

    const target = event.target;
    const elementInfo = {
      tag: target.tagName.toLowerCase(),
      id: target.id,
      className: target.className,
      text: target.textContent?.substring(0, 50),
      type: target.type,
      x: event.clientX,
      y: event.clientY
    };

    interactionCount.current++;
    lastInteractionTime.current = Date.now();

    trackEvent('micro_interaction_click', {
      component: componentName,
      element: elementInfo,
      interaction_count: interactionCount.current,
      page: getCurrentPage()
    });
  }, [componentName, trackClicks]);

  // Track hover interactions
  const trackHover = useCallback((event, action) => {
    if (!trackHovers) return;

    throttle('hover', () => {
      const target = event.target;
      trackEvent('micro_interaction_hover', {
        component: componentName,
        action: action, // 'enter' or 'leave'
        element: {
          tag: target.tagName.toLowerCase(),
          id: target.id,
          className: target.className
        },
        page: getCurrentPage()
      });
    }, 500);
  }, [componentName, trackHovers, throttle]);

  // Track focus interactions
  const trackFocusInteraction = useCallback((event, action) => {
    if (!trackFocus) return;

    const target = event.target;
    trackEvent('micro_interaction_focus', {
      component: componentName,
      action: action, // 'focus' or 'blur'
      element: {
        tag: target.tagName.toLowerCase(),
        id: target.id,
        type: target.type,
        name: target.name
      },
      page: getCurrentPage()
    });
  }, [componentName, trackFocus]);

  // Track scroll interactions
  const trackScrollInteraction = useCallback(() => {
    if (!trackScrolling) return;

    throttle('scroll', () => {
      const scrollPercentage = Math.round(
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
      );

      trackEvent('micro_interaction_scroll', {
        component: componentName,
        scroll_percentage: scrollPercentage,
        scroll_y: window.scrollY,
        page: getCurrentPage()
      });
    }, 2000);
  }, [componentName, trackScrolling, throttle]);

  // Track keystroke patterns
  const trackKeystroke = useCallback((event) => {
    if (!trackKeystrokes) return;

    // Don't track actual key values for privacy
    const keyInfo = {
      key_type: event.key.length === 1 ? 'character' : 'special',
      key_code: event.keyCode,
      ctrl_key: event.ctrlKey,
      alt_key: event.altKey,
      shift_key: event.shiftKey
    };

    throttle('keystroke', () => {
      trackEvent('micro_interaction_keystroke', {
        component: componentName,
        key_info: keyInfo,
        target_type: event.target.tagName.toLowerCase(),
        page: getCurrentPage()
      });
    }, 500);
  }, [componentName, trackKeystrokes, throttle]);

  // Track mouse movement patterns
  const trackMouseMove = useCallback((event) => {
    if (!trackMouseMovement) return;

    throttle('mouse_move', () => {
      trackEvent('micro_interaction_mouse_move', {
        component: componentName,
        x: event.clientX,
        y: event.clientY,
        movement_speed: Math.sqrt(
          Math.pow(event.movementX, 2) + Math.pow(event.movementY, 2)
        ),
        page: getCurrentPage()
      });
    }, 3000);
  }, [componentName, trackMouseMovement, throttle]);

  // Set up event listeners
  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const handlers = {};

    if (trackClicks) {
      handlers.click = trackClick;
      element.addEventListener('click', handlers.click);
    }

    if (trackHovers) {
      handlers.mouseenter = (e) => trackHover(e, 'enter');
      handlers.mouseleave = (e) => trackHover(e, 'leave');
      element.addEventListener('mouseenter', handlers.mouseenter);
      element.addEventListener('mouseleave', handlers.mouseleave);
    }

    if (trackFocus) {
      handlers.focusin = (e) => trackFocusInteraction(e, 'focus');
      handlers.focusout = (e) => trackFocusInteraction(e, 'blur');
      element.addEventListener('focusin', handlers.focusin);
      element.addEventListener('focusout', handlers.focusout);
    }

    if (trackScrolling) {
      handlers.scroll = trackScrollInteraction;
      window.addEventListener('scroll', handlers.scroll);
    }

    if (trackKeystrokes) {
      handlers.keydown = trackKeystroke;
      element.addEventListener('keydown', handlers.keydown);
    }

    if (trackMouseMovement) {
      handlers.mousemove = trackMouseMove;
      element.addEventListener('mousemove', handlers.mousemove);
    }

    // Cleanup
    return () => {
      Object.entries(handlers).forEach(([event, handler]) => {
        if (event === 'scroll') {
          window.removeEventListener(event, handler);
        } else {
          element.removeEventListener(event, handler);
        }
      });

      // Clear any pending throttled calls
      Object.values(throttleTimers.current).forEach(timer => {
        clearTimeout(timer);
      });
    };
  }, [
    trackClicks, trackHovers, trackFocus, trackScrolling, 
    trackKeystrokes, trackMouseMovement,
    trackClick, trackHover, trackFocusInteraction, 
    trackScrollInteraction, trackKeystroke, trackMouseMove
  ]);

  // Track engagement summary on unmount
  useEffect(() => {
    return () => {
      if (interactionCount.current > 0) {
        const engagementTime = Date.now() - lastInteractionTime.current;
        trackEngagement('component_interaction_summary', {
          component: componentName,
          total_interactions: interactionCount.current,
          engagement_time: engagementTime,
          page: getCurrentPage()
        });
      }
    };
  }, [componentName]);

  return {
    ref: elementRef,
    interactionCount: interactionCount.current,
    lastInteractionTime: lastInteractionTime.current
  };
};

// Hook for tracking form-specific micro-interactions
export const useFormInteractionTracking = (formName) => {
  const fieldInteractions = useRef({});
  const formStartTime = useRef(Date.now());
  const formErrors = useRef({});

  const trackFieldInteraction = useCallback((fieldName, action, value = null, error = null) => {
    if (!fieldInteractions.current[fieldName]) {
      fieldInteractions.current[fieldName] = {
        focus_count: 0,
        change_count: 0,
        error_count: 0,
        first_interaction: Date.now(),
        last_interaction: Date.now()
      };
    }

    const field = fieldInteractions.current[fieldName];
    field.last_interaction = Date.now();

    switch (action) {
      case 'focus':
        field.focus_count++;
        break;
      case 'change':
        field.change_count++;
        break;
      case 'error':
        field.error_count++;
        formErrors.current[fieldName] = error;
        break;
    }

    trackEvent('form_field_interaction', {
      form_name: formName,
      field_name: fieldName,
      action: action,
      field_stats: field,
      has_value: !!value,
      value_length: value ? value.toString().length : 0,
      error_message: error,
      page: getCurrentPage()
    });
  }, [formName]);

  const trackFormSubmission = useCallback((success, errors = {}) => {
    const formDuration = Date.now() - formStartTime.current;
    const totalInteractions = Object.values(fieldInteractions.current)
      .reduce((sum, field) => sum + field.focus_count + field.change_count, 0);

    trackEvent('form_submission', {
      form_name: formName,
      success: success,
      form_duration: formDuration,
      total_interactions: totalInteractions,
      field_count: Object.keys(fieldInteractions.current).length,
      error_count: Object.keys(errors).length,
      errors: errors,
      page: getCurrentPage()
    });
  }, [formName]);

  const getFormAnalytics = useCallback(() => {
    const formDuration = Date.now() - formStartTime.current;
    const fieldsWithInteractions = Object.keys(fieldInteractions.current).length;
    const totalInteractions = Object.values(fieldInteractions.current)
      .reduce((sum, field) => sum + field.focus_count + field.change_count, 0);
    const totalErrors = Object.values(fieldInteractions.current)
      .reduce((sum, field) => sum + field.error_count, 0);

    return {
      form_duration: formDuration,
      fields_interacted: fieldsWithInteractions,
      total_interactions: totalInteractions,
      total_errors: totalErrors,
      field_details: fieldInteractions.current,
      current_errors: formErrors.current
    };
  }, []);

  return {
    trackFieldInteraction,
    trackFormSubmission,
    getFormAnalytics
  };
};

// Hook for tracking calculator-specific interactions
export const useCalculatorInteractionTracking = (calculatorName) => {
  const calculationAttempts = useRef(0);
  const inputChanges = useRef(0);
  const errorOccurrences = useRef(0);

  const trackCalculationAttempt = useCallback((inputData, success, result = null, error = null) => {
    calculationAttempts.current++;
    
    if (!success) {
      errorOccurrences.current++;
    }

    trackEvent('calculator_interaction', {
      calculator_name: calculatorName,
      action: 'calculation_attempt',
      attempt_number: calculationAttempts.current,
      success: success,
      input_fields: Object.keys(inputData).length,
      has_result: !!result,
      error_type: error?.type,
      error_message: error?.message,
      page: getCurrentPage()
    });
  }, [calculatorName]);

  const trackInputChange = useCallback((fieldName, oldValue, newValue) => {
    inputChanges.current++;

    trackEvent('calculator_input_change', {
      calculator_name: calculatorName,
      field_name: fieldName,
      change_number: inputChanges.current,
      value_type: typeof newValue,
      value_length: newValue ? newValue.toString().length : 0,
      is_increase: newValue > oldValue,
      page: getCurrentPage()
    });
  }, [calculatorName]);

  const getCalculatorStats = useCallback(() => {
    return {
      calculation_attempts: calculationAttempts.current,
      input_changes: inputChanges.current,
      error_occurrences: errorOccurrences.current,
      success_rate: calculationAttempts.current > 0 
        ? ((calculationAttempts.current - errorOccurrences.current) / calculationAttempts.current) * 100 
        : 0
    };
  }, []);

  return {
    trackCalculationAttempt,
    trackInputChange,
    getCalculatorStats
  };
};

export default useMicroInteractionTracking;